import { useEffect, useMemo, useState } from "react";
import { Collapse, Table, Typography, Button, Input, Tag } from "antd";
import { DeleteOutlined, EditOutlined, SettingOutlined } from "@ant-design/icons";
import { COLORS } from "../../../utils/constants";
import NumberInput from "../../common/NumberInput";
import { FoulBuildUpRowData } from "../../../types/tender.types";
import { formatUnit } from "../../../utils/constants";
import { formatCurrency } from "../../../utils/calculations";
import { StormBuildUpRowDef } from "../../../utils/stormBuildUpTypes";
import { useDevMode } from "@/contexts/DevModeContext";
import { useTender } from "../../../contexts/TenderContext";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { findPondsSwalesEvaluateCode, findHeadwallEvaluateCode } from "../../../utils/stormPondsSwalesEvaluateCodesMapping";
import { findCellularAttenuationEvaluateCode } from "../../../utils/stormCellularAttenuationEvaluateCodesMapping";
import { findWeholiteAttenuationEvaluateCode } from "../../../utils/stormWeholiteAttenuationEvaluateCodesMapping";
import { resolveConcAttenuationEvaluateCode } from "../../../utils/stormConcAttenuationEvaluateCodesMapping";
import { findBoxCulvertsEvaluateCode } from "../../../utils/stormBoxCulvertsEvaluateCodesMapping";

interface StormBuildUpSection {
  headerId: string;
  headerDescription: string;
  headerSuffix?: string;
  rows: StormBuildUpRowDef[];
}

// Each header row (e.g. "POND 1") starts a new group, matching how POS.tsx
// turns each "PUBLIC OPEN SPACE N" header into its own Collapse.Panel.
const groupRowsIntoSections = (rows: StormBuildUpRowDef[]): StormBuildUpSection[] => {
  const sections: StormBuildUpSection[] = [];
  let current: StormBuildUpSection | null = null;
  for (const row of rows) {
    if (row.isHeader) {
      current = { headerId: row.id, headerDescription: row.description, headerSuffix: row.titleSuffix, rows: [] };
      sections.push(current);
      continue;
    }
    current?.rows.push(row);
  }
  return sections;
};

// Appended (user-typed) text on a section title is shown as a coloured tag so it is
// obvious it was added to the standard title.
const renderSectionTitle = (section: StormBuildUpSection): React.ReactNode => {
  const suffix = section.headerSuffix;
  if (!suffix || !section.headerDescription.endsWith(suffix)) return section.headerDescription;
  return (
    <>
      {section.headerDescription.slice(0, section.headerDescription.length - suffix.length).trimEnd()}
      <span
        style={{
          marginLeft: 8,
          padding: '0 8px',
          borderRadius: 4,
          background: '#fff7e6',
          border: '1px solid #ffd591',
          color: '#d46b08',
          fontWeight: 600,
        }}
      >
        {suffix}
      </span>
    </>
  );
};

export interface StormBuildUpTableProps {
  rows: StormBuildUpRowDef[];
  getRow: (id: string, defaultQuant: number | null) => FoulBuildUpRowData;
  updateRow: (id: string, field: 'quant' | 'evaluateCode' | 'quantText', value: number | null | string) => void;
  sectionTitle?: string;
  /** Top offset (px) of the page's own sticky header, e.g. StormSewersTakeoff's `stickyOffset`. */
  stickyOffset: number;
  /** Expanded panel keys — lifted to the parent so an Expand All/Collapse All control can live next to the table's own switcher, outside this component. */
  activeKeys: string[];
  onActiveKeysChange: (keys: string[]) => void;
  /** When set, each panel header shows a delete icon that calls this with the section's headerId (e.g. `pond_2_hdr`) — used by PondsTable to let sections added beyond the default count be removed again. */
  onDeleteSection?: (headerId: string) => void;
  /** When set, each panel header shows a gear/Configure icon that calls this with the section's headerId — used by PondsTable to open its per-section thickness Configure modal (same header-icon pattern as POS.tsx). */
  onConfigureSection?: (headerId: string) => void;
  /** When set (and not in view mode), each section title shows a pencil for inline editing; saving calls this with the section's headerId and the typed text (empty = remove) — used by PondsTable/SwalesTable, same pattern as Pump Station's titles. */
  onEditSectionTitle?: (headerId: string, text: string) => void;
  /** When true, prepends a blank "Item" column before Description — used by BoxCulvertsTable. Intentionally empty for now; content TBD. */
  showItemColumn?: boolean;
}

export default function StormBuildUpTable({ rows, getRow, updateRow, sectionTitle = 'Description', stickyOffset, activeKeys, onActiveKeysChange, onDeleteSection, onConfigureSection, onEditSectionTitle, showItemColumn = false }: StormBuildUpTableProps) {
  const showDevMode = useDevMode();
  const { state } = useTender();
  const sections = useMemo(() => groupRowsIntoSections(rows), [rows]);
  const [panelHeaderOffset, setPanelHeaderOffset] = useState(stickyOffset);

  // Inline section-title editing (pencil next to the title) — default title stays, typed text is the suffix.
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const saveTitle = (headerId: string) => {
    onEditSectionTitle?.(headerId, titleDraft.trim());
    setEditingTitleId(null);
  };
  const renderTitle = (section: StormBuildUpSection): React.ReactNode => {
    if (!onEditSectionTitle) return renderSectionTitle(section);
    const suffix = section.headerSuffix;
    const baseTitle = suffix && section.headerDescription.endsWith(suffix)
      ? section.headerDescription.slice(0, section.headerDescription.length - suffix.length).trimEnd()
      : section.headerDescription;
    if (editingTitleId === section.headerId) {
      return (
        <span onClick={(e) => e.stopPropagation()}>
          <Input
            autoFocus
            size="small"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onPressEnter={() => saveTitle(section.headerId)}
            onBlur={() => saveTitle(section.headerId)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditingTitleId(null); }}
            placeholder="Text to append to title"
            addonBefore={baseTitle}
            className="section-title-input"
            style={{ width: 380 }}
          />
        </span>
      );
    }
    return (
      <>
        {renderSectionTitle(section)}
        {!state.isViewMode && (
          <EditOutlined
            title="Append text to title"
            style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }}
            onClick={(e) => { e.stopPropagation(); setTitleDraft(suffix ?? ''); setEditingTitleId(section.headerId); }}
          />
        )}
      </>
    );
  };

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code column is actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    // A few evaluate codes are combined (e.g. "ALPE010+L001B") — sum whichever
    // parts resolve to a library rate rather than requiring an exact combined match.
    if (code.includes('+')) {
      const parts = code.split('+').map((c) => resourceCodeMap.get(c.trim().toUpperCase())).filter((r): r is number => r !== undefined);
      return parts.length > 0 ? parts.reduce((sum, r) => sum + r, 0) : undefined;
    }
    return resourceCodeMap.get(code.trim().toUpperCase());
  };

  useEffect(() => {
    const headerRowEl = document.querySelector('.storm-buildup-shared-header-wrapper') as HTMLElement | null;
    setPanelHeaderOffset(stickyOffset + (headerRowEl?.offsetHeight ?? 0));
  }, [stickyOffset, sections]);

  const columns = [
    ...(showItemColumn ? [{
      title: <span style={{ fontWeight: 700, fontSize: 13 }}>Item</span>,
      dataIndex: 'item',
      key: 'item',
      width: 70,
      align: 'center' as const,
      render: (_: string, record: StormBuildUpRowDef) => record.itemLabel ?? null,
    }] : []),
    {
      title: <span style={{ fontWeight: 700, fontSize: 13 }}>{sectionTitle}</span>,
      dataIndex: 'description',
      key: 'description',
      render: (_: string, record: StormBuildUpRowDef) => (
        record.isSubHeader
          ? <Typography.Text strong className="text-indigo-600">{record.description}</Typography.Text>
          : <Typography.Text>{record.description}</Typography.Text>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'id',
      key: 'quant',
      width: 120,
      align: 'center' as const,
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isSubHeader || record.noQuant) return null;
        if (record.allowTextQuant) {
          const { quantText } = getRow(record.id, record.defaultQuant);
          return (
            <Input
              value={quantText ?? ''}
              onChange={(e) => updateRow(record.id, 'quantText', e.target.value)}
              placeholder="—"
              style={{ width: '100%', textAlign: 'right' }}
            />
          );
        }
        const { quant } = getRow(record.id, record.defaultQuant);
        return (
          <NumberInput
            value={quant ?? 0}
            onChange={(val) => updateRow(record.id, 'quant', val)}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: string, record: StormBuildUpRowDef) => (
        record.isSubHeader || record.noQuant ? null : <span style={{ color: '#6b7280' }}>{formatUnit(record.unit)}</span>
      ),
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'id',
      key: 'evaluateCode',
      width: 160,
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isSubHeader || record.noQuant) return null;
        const isPondOrSwale = record.id.startsWith('pond_') || record.id.startsWith('swale_');
        const isHeadwall = record.id.startsWith('hw_');
        const isCellularAttenuation = record.id.startsWith('ca_');
        const isWeholiteAttenuation = record.id.startsWith('wh_');
        const isConcAttenuation = record.id.startsWith('conc_');
        const isBoxCulvert = record.id.startsWith('bc_');
        const evaluateCode = isPondOrSwale
          ? findPondsSwalesEvaluateCode(record.description) ?? ''
          : isHeadwall
          ? findHeadwallEvaluateCode(record.description) ?? ''
          : isCellularAttenuation
          ? findCellularAttenuationEvaluateCode(record.description) ?? ''
          : isWeholiteAttenuation
          ? findWeholiteAttenuationEvaluateCode(record.id) ?? ''
          : isConcAttenuation
          ? resolveConcAttenuationEvaluateCode(record) ?? ''
          : isBoxCulvert
          ? findBoxCulvertsEvaluateCode(record.description) ?? ''
          : getRow(record.id, record.defaultQuant).evaluateCode ?? '';
        if (!evaluateCode) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(evaluateCode);
        return (
          <span>
            <Tag color="blue">{evaluateCode}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  return (
    <div className="storm-buildup-accordion">
      {/* Shared table header — every panel's table uses the same columns, so
          render the header once, sticky above the accordion list, instead of
          repeating it inside each panel (same pattern as POS.tsx). */}
      <div className="storm-buildup-shared-header-wrapper" style={{ position: 'sticky', top: `${stickyOffset}px`, zIndex: 95 }}>
        <Table<StormBuildUpRowDef>
          className="storm-buildup-shared-header"
          bordered
          size="middle"
          showHeader
          pagination={false}
          rowKey="id"
          dataSource={[]}
          columns={columns}
          locale={{ emptyText: '' }}
        />
      </div>

      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => onActiveKeysChange(Array.isArray(keys) ? keys as string[] : keys ? [keys as string] : [])}
        className="storm-buildup-collapse"
        size="large"
        bordered={false}
        expandIconPosition="start"
        expandIcon={({ isActive }) => (
          <span className={`storm-buildup-expand-icon${isActive ? '' : ' storm-buildup-expand-icon-collapsed'}`} />
        )}
      >
        {sections.map((section) => (
          <Collapse.Panel
            key={section.headerId}
            header={
              onDeleteSection || onConfigureSection ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Typography.Text strong className="text-indigo-600">{renderTitle(section)}</Typography.Text>
                  <span onClick={(e) => e.stopPropagation()}>
                    {onConfigureSection && (
                      <Button
                        type="text"
                        size="small"
                        className="configure-headers-btn"
                        icon={<SettingOutlined style={{ color: COLORS.primary }} />}
                        onClick={() => onConfigureSection(section.headerId)}
                      />
                    )}
                    {onDeleteSection && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => onDeleteSection(section.headerId)}
                      />
                    )}
                  </span>
                </div>
              ) : (
                <Typography.Text strong className="text-indigo-600">{renderTitle(section)}</Typography.Text>
              )
            }
          >
            <Table<StormBuildUpRowDef>
              className="storm-buildup-table"
              bordered
              size="middle"
              showHeader={false}
              pagination={false}
              rowKey="id"
              dataSource={section.rows}
              columns={columns}
              rowClassName={(record) => record.isSubHeader ? 'storm-buildup-sub-header-row' : ''}
            />
          </Collapse.Panel>
        ))}
      </Collapse>

      <style>{`
        .storm-buildup-collapse { background: transparent; }
        .storm-buildup-collapse > .ant-collapse-item {
          margin-bottom: 0;
          background: #fff;
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .storm-buildup-collapse > .ant-collapse-item:first-child {
          border-top: 1px solid #e5e7eb !important;
        }
        .storm-buildup-collapse > .ant-collapse-item:hover {
          border-color: #c7d2fe !important;
        }
        .storm-buildup-collapse .ant-collapse-content-box { padding: 0 !important; }
        .storm-buildup-collapse .ant-collapse-content {
          border-top: 1px solid #e5e7eb !important;
          border-radius: 0;
        }
        /* Sticky accordion panel headers — must sit above table cell stacking
           contexts, but below StepperNavigation.tsx's fixed bar (z-index: 99,
           top: 88px) so it doesn't paint through the stepper while scrolling. */
        .storm-buildup-collapse .ant-collapse-header {
          position: sticky !important;
          top: ${panelHeaderOffset}px !important;
          z-index: 90 !important;
          align-items: center !important;
          padding: 14px 20px !important;
          background-color: #eef2ff !important;
          border-radius: 0 !important;
        }
        .storm-buildup-collapse .ant-collapse-item-active > .ant-collapse-header {
          border-bottom: 1px solid #e0e7ff !important;
        }
        .storm-buildup-expand-icon {
          position: relative;
          display: inline-block;
          width: 17px;
          height: 17px;
          background: #fff;
          border: 1px solid #d9d9d9;
          border-radius: 50%;
          color: rgba(0, 0, 0, 0.88);
          cursor: pointer;
          transform: scale(0.94);
        }
        .storm-buildup-expand-icon::before,
        .storm-buildup-expand-icon::after {
          content: '';
          position: absolute;
          background: currentcolor;
          transition: transform 0.2s ease-out;
        }
        .storm-buildup-expand-icon::before {
          top: 7px;
          left: 1px;
          right: 1px;
          height: 1px;
        }
        .storm-buildup-expand-icon::after {
          top: 1px;
          bottom: 1px;
          left: 7px;
          width: 1px;
          transform: rotate(90deg);
        }
        .storm-buildup-expand-icon-collapsed::before {
          transform: rotate(-180deg);
        }
        .storm-buildup-expand-icon-collapsed::after {
          transform: rotate(0deg);
        }
        .storm-buildup-table {
          border-radius: 0;
          overflow: hidden;
        }
        .storm-buildup-table .storm-buildup-sub-header-row > td {
          background-color: #eff6ff !important;
          font-weight: 600 !important;
        }
        .storm-buildup-table .ant-table-thead > tr > th {
          z-index: 2 !important;
          will-change: auto !important;
          transform: none !important;
        }
        .storm-buildup-shared-header-wrapper { background-color: #fff; }
        .storm-buildup-shared-header .ant-table-tbody,
        .storm-buildup-shared-header .ant-table-placeholder {
          display: none !important;
        }
        .storm-buildup-shared-header .ant-table {
          border-radius: 0;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
